import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiClient, ApiClientError } from './client';

/** The Meta catalog connected to a WhatsApp channel, with sync counts. */
export interface CatalogStatus {
  channelId: string;
  catalogId: string | null;
  catalogName: string | null;
  connectedAt: string | null;
  /** Used in the catalog for products without their own photo. */
  fallbackImageAssetId: string | null;
  counts: { synced: number; failed: number };
  /** When the catalogue was last written to. */
  lastSyncedAt: string | null;
  /** Opens the catalogue in Meta Commerce Manager. */
  commerceManagerUrl: string | null;
}

/** A catalogue on the channel's WhatsApp Business Account (before connecting). */
export interface AvailableCatalog {
  id: string;
  name: string | null;
  productCount: number | null;
  connected: boolean;
  commerceManagerUrl: string;
}

/** An item already in the connected catalogue, for taking it over. */
export interface TakeoverItem {
  retailerId: string;
  name: string;
  description: string | null;
  price: number | null;
  currency: string | null;
  imageUrl: string | null;
  linkedProductId: string | null;
  suggestedProductId: string | null;
  suggestedBy: 'ID' | 'SKU' | 'NAME' | null;
}

export type TakeoverDecision =
  | { retailerId: string; action: 'LINK'; productId: string }
  | {
      retailerId: string;
      action: 'IMPORT';
      name: string;
      description?: string | null;
      price?: number | null;
      currency?: string | null;
      imageUrl?: string | null;
    };

export interface ProductCatalogSync {
  productServiceId: string;
  channelId: string;
  status: 'SYNCED' | 'FAILED' | 'REMOVED';
  errorMessage: string | null;
  syncedAt: string;
}

const KEY = ['whatsapp-catalog'] as const;

export function useCatalogStatus(channelId: string) {
  return useQuery<CatalogStatus, ApiClientError>({
    queryKey: [...KEY, 'status', channelId],
    queryFn: () => apiClient.get<CatalogStatus>(`/channels/whatsapp/${channelId}/catalog`),
  });
}

function useCatalogAction(channelId: string, action: 'sync' | 'disconnect') {
  const qc = useQueryClient();
  return useMutation<CatalogStatus, ApiClientError, void>({
    mutationFn: () =>
      apiClient.post<CatalogStatus>(`/channels/whatsapp/${channelId}/catalog/${action}`),
    onSettled: () => qc.invalidateQueries({ queryKey: KEY }),
  });
}

/** Connect one of the account's catalogues (default: the first). */
export function useConnectCatalog(channelId: string) {
  const qc = useQueryClient();
  return useMutation<CatalogStatus, ApiClientError, string | void>({
    mutationFn: (catalogId) =>
      apiClient.post<CatalogStatus>(
        `/channels/whatsapp/${channelId}/catalog/connect`,
        catalogId ? { catalogId } : undefined,
      ),
    onSettled: () => qc.invalidateQueries({ queryKey: KEY }),
  });
}

export function useAvailableCatalogs(channelId: string, enabled: boolean) {
  return useQuery<AvailableCatalog[], ApiClientError>({
    queryKey: [...KEY, 'available', channelId],
    queryFn: () =>
      apiClient.get<AvailableCatalog[]>(`/channels/whatsapp/${channelId}/catalog/available`),
    enabled,
  });
}

export interface TakeoverPreview {
  catalogId: string;
  truncated: boolean;
  items: TakeoverItem[];
  /** Every product an item can be linked to. */
  products: { id: string; name: string }[];
}

export function useTakeoverPreview(channelId: string, enabled: boolean) {
  return useQuery<TakeoverPreview, ApiClientError>({
    queryKey: [...KEY, 'takeover', channelId],
    queryFn: () => apiClient.get(`/channels/whatsapp/${channelId}/catalog/takeover`),
    enabled,
  });
}

export function useApplyTakeover(channelId: string) {
  const qc = useQueryClient();
  return useMutation<
    { linked: number; imported: number; status: CatalogStatus },
    ApiClientError,
    TakeoverDecision[]
  >({
    mutationFn: (decisions) =>
      apiClient.post(`/channels/whatsapp/${channelId}/catalog/takeover`, { decisions }),
    onSettled: () => {
      void qc.invalidateQueries({ queryKey: KEY });
      void qc.invalidateQueries({ queryKey: ['products-services'] });
    },
  });
}
export const useSyncCatalog = (channelId: string) => useCatalogAction(channelId, 'sync');
export const useDisconnectCatalog = (channelId: string) =>
  useCatalogAction(channelId, 'disconnect');

export function useSetCatalogFallbackImage(channelId: string) {
  const qc = useQueryClient();
  return useMutation<CatalogStatus, ApiClientError, string | null>({
    mutationFn: (imageAssetId) =>
      apiClient.put<CatalogStatus>(`/channels/whatsapp/${channelId}/catalog/fallback-image`, {
        imageAssetId,
      }),
    onSettled: () => qc.invalidateQueries({ queryKey: KEY }),
  });
}

/** Every product's sync state - the Products page's "WhatsApp catalog" column. */
export function useCatalogSyncStatuses(enabled = true) {
  return useQuery<ProductCatalogSync[], ApiClientError>({
    queryKey: [...KEY, 'sync-status'],
    queryFn: () => apiClient.get<ProductCatalogSync[]>('/catalog/sync-status'),
    enabled,
  });
}
